import * as _ from 'underscore';

import S, { DataSignal } from 's-js';
import { CaseInsensitiveContains } from 'lib/diacriticals';
import TZDate from 'lib/TZDate';
import { TZUtils } from 'lib/tzutils';
import { tzmomentMod } from 'lib/tzmoment';
import { IAjax } from 'lib/Ajax';

import { DomainSummary } from 'data/models/DomainSummary';
import { HomepageTabVisibility } from 'server/enums/HomepageTabVisibility';
import { UserSummary } from 'data/models/UserSummary';

import { PCCache } from 'data/PCCache';

import { Entrypoints } from 'core/app/entrypoints';

import { Typeahead } from 'portcall/typeahead/model';
import { Login, LoginParams } from 'portcall/login/model';
import { PortCallSchedule, PortCallScheduleParams } from 'portcall/portcallschedule/model';

import { Movements, MovementsLoader } from './movements';
import { InPortVessels } from './inport';
import { CruiseSchedule } from './cruiseschedule';
import { Map } from './map';

const FilterStorageKey = 'portcall.homepage.filter';
const CookieComplianceKey = 'portcall.cookie.compliance';

export enum HomePageTab {
    Movements,
    InPort,
    Cruise,
    Map
};

const HomePageTabs = [ 
    HomePageTab.Movements,
    HomePageTab.InPort,
    HomePageTab.Cruise,
    HomePageTab.Map
];

export interface HomePageParams {
    tab : HomePageTab,
    date : TZDate,
    port : string | undefined
}

export type HomePage = ReturnType<typeof HomePage>;
export function HomePage(
    params : HomePageParams,
    domain : DomainSummary, 
    user : DataSignal<UserSummary | null>, 
    initialDataLoaded : () => boolean,
    data : PCCache,
    tzutils : TZUtils, 
    entrypoints : Entrypoints,
    ajax : IAjax,
    movementsLoader : MovementsLoader,
    portcallScheduleModel : (params : PortCallScheduleParams) => PortCallSchedule,
    loginModel : (params : LoginParams) => Login
) {
    var tab            = S.value(tabVisible(params.tab) ? params.tab : HomePageTabs.find(tabVisible) || HomePageTab.Movements),
        portFilter     = S.value(params.port || localStorage[FilterStorageKey] as string | undefined || ''),
        date           = S.value(params.date),
        now            = tzutils.now(),
        portTypeahead  = new Typeahead({
                typeaheadOptions: {
                minLength: 0,
                highlight: true,
                hint: false
            },
            collection: () => Array.from(data.Ports.items()),
            observable: portFilter,
            matchFn: (port, query) => CaseInsensitiveContains(port.Name(), query),
            displayKey: port => port.Name()
        }),
        movements = Movements(movementsLoader, data, date, user, portFilterer, () => tab() === HomePageTab.Movements, tzutils, portcallScheduleModel),
        inPortVessels = InPortVessels(ajax, now, portFilterer, entrypoints, function () { return tab() === HomePageTab.InPort; }),
        cruiseSchedule = CruiseSchedule(ajax, portFilterer, entrypoints, function () { return tab() === HomePageTab.Cruise; }),
        map = Map(() => tab() === HomePageTab.Map, data.Locations.items, domain.Settings()!.Map),
        showLogin = S.data(false),
        login = S(() => showLogin() ? loginModel({ useLandingPage: true, onHidePopup: () => showLogin(false) }) : null),
        numberOfPorts = () => data.Ports.items().size,
        showCompliancy = S.value(localStorage[CookieComplianceKey] as string | undefined || 'not compliant'),
        isCompliant = () => showCompliancy() === 'compliant' ? true : false;

    // Store port filter
    S(() => localStorage[FilterStorageKey] = portFilter());

    // Store cookie compliance
    S(() => localStorage[CookieComplianceKey] = showCompliancy());

    var vm = {
        Domain: domain,
        User: user,
        Tab: tab,
        TabVisible: tabVisible,
        PortFilter: portFilter,
        Date: date,
        Now: now,
        DateToday: function DateToday() { date(tzutils.today()); },
        DateNext:  function DateNext()  { date(tzmomentMod(tzutils, date(), m => m.add(1, 'd'))); },
        DatePrev:  function DatePrev()  { date(tzmomentMod(tzutils, date(), m => m.add(-1, 'd'))); },
        GoToPortCall: goToPortCall,
        ShowLogin: showLogin,
        PortTypeahead: portTypeahead,
        Movements: movements,
        InPortVessels: inPortVessels,
        CruiseSchedule: cruiseSchedule,
        Map: map,
        Login: login,
        NumberOfPorts: numberOfPorts,
        InitialDataLoaded : initialDataLoaded,
        ShowCompliancy : showCompliancy,
        IsCompliant : isCompliant
    }

    return vm;

    function portFilterer<T>(list : () => T[], portName : (i : T) => string) {
        return S(function () {
            return _.filter(list(), function (i) {
                return !portFilter() || CaseInsensitiveContains(portName(i), portFilter());
            });
        });
    }
    
    function goToPortCall(pc : { PortCallId : number | undefined }) {
        if (!user() || pc.PortCallId == undefined) return;
        window.location.hash = "#!portcallprofile?id=" + pc.PortCallId;
    }
    
    function tabVisible(tab : HomePageTab) {
        var settings = domain.Settings() ? domain.Settings()!.Homepage : null;
        return  settings === null      ? false :
                tab === HomePageTab.Movements ? tabLogInVisibility(settings.MovementsVisibility()) :
                tab === HomePageTab.InPort    ? tabLogInVisibility(settings.InPortVisibility()) :
                tab === HomePageTab.Cruise    ? tabLogInVisibility(settings.CruiseVisibility()) :
                tab === HomePageTab.Map       ? tabLogInVisibility(settings.MapVisibility()) :
                false;
    }
    
    function tabLogInVisibility(code : HomepageTabVisibility) {
        return code === HomepageTabVisibility.Public
            || (code === HomepageTabVisibility.LoggedIn && user() !== null);
    }
}

export type PortFilterer = <T>(list: () => T[], portName : (i : T) => string) => () => T[]

export type GoToPortCall = (item : { PortCallId : number }) => void



// WEBPACK FOOTER //
// ./apps/portcall/homepage/model.ts